import React from "react";
import { cn } from "../../../lib/utils";
import {
  FormField,
  Input,
  Select,
  Textarea,
} from "../../../shared/ui/composites";
import SearchableSelect from "../../../shared/ui/composites/SearchableSelect";
import { getFieldLabel, resolveOptions } from "../utils/schemaForm";
import JobCodeEditor from "./JobCodeEditor";

const LTR_KEY_RE =
  /^(id|cron|timezone|email|url|host|user|pass|password|config_json|from|method|driver|options_job_id|provider_ids|code|function_code|formula_key|formula|key)$/i;

function isLtrField(field) {
  if (field?.dir === "ltr") return true;
  if (field?.dir === "rtl") return false;
  return LTR_KEY_RE.test(String(field?.key || ""));
}

export default function SchemaField({
  field,
  value,
  onChange,
  error,
  allSettings = [],
  formValues = {},
  disabled = false,
}) {
  if (field?.hidden) return null;

  const id = `schema-field-${field.key}`;
  const label = getFieldLabel(field);
  const editable = field.editable !== false && !disabled;
  const options = resolveOptions(field, allSettings, formValues);
  const ltr = isLtrField(field);
  const useSearchable =
    field.searchable === true ||
    (field.type === "select" && options.length > 12);

  const commonProps = {
    id,
    disabled: !editable,
    className: cn(
      error && "border-red-400",
      ltr && "dir-ltr text-left font-mono text-sm",
    ),
    dir: ltr ? "ltr" : undefined,
  };

  let control = null;

  switch (field.type) {
    case "boolean":
      control = (
        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input
            id={id}
            type="checkbox"
            checked={Boolean(value)}
            disabled={!editable}
            onChange={(e) => onChange(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300"
          />
          <span>{label}</span>
        </label>
      );
      break;

    case "select": {
      const commitSelect = (next) => {
        if (next === "" || next == null) {
          onChange("");
          return;
        }
        const matched = options.find((option) => String(option.value) === String(next));
        const sample = matched?.value;
        onChange(
          field.item_type === "number" || typeof sample === "number"
            ? Number(next)
            : next,
        );
      };
      control = useSearchable ? (
        <SearchableSelect
          id={id}
          disabled={!editable}
          error={Boolean(error)}
          value={value === null || value === undefined ? "" : value}
          options={options}
          onChange={commitSelect}
          allowCustom={field.allow_custom === true}
          placeholder="انتخاب کنید یا شناسه بنویسید"
          emptyLabel="موردی یافت نشد"
        />
      ) : (
        <Select
          variant="form"
          {...commonProps}
          value={value === null || value === undefined ? "" : String(value)}
          onChange={(e) => commitSelect(e.target.value)}
        >
          <option value="">انتخاب کنید</option>
          {options.map((option) => (
            <option key={String(option.value)} value={String(option.value)}>
              {option.label}
            </option>
          ))}
        </Select>
      );
      break;
    }

    case "multiselect":
      control = (
        <Select
          variant="form"
          size="list"
          multiple
          {...commonProps}
          value={(Array.isArray(value) ? value : []).map(String)}
          onChange={(e) => {
            const selected = Array.from(e.target.selectedOptions).map((opt) => {
              const raw = opt.value;
              const sample = options.find((o) => String(o.value) === raw)?.value;
              return typeof sample === "number" ? Number(raw) : raw;
            });
            onChange(selected);
          }}
        >
          {options.map((option) => (
            <option key={String(option.value)} value={String(option.value)}>
              {option.label}
            </option>
          ))}
        </Select>
      );
      break;

    case "tags":
    case "array":
      control = (
        <Textarea
          {...commonProps}
          rows={3}
          value={Array.isArray(value) ? value.join(", ") : value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="مقادیر را با کاما جدا کنید"
        />
      );
      break;

    case "code":
      control = (
        <JobCodeEditor
          id={id}
          value={value ?? ""}
          disabled={!editable}
          error={Boolean(error)}
          onChange={onChange}
          title={label}
          collapsed={field.collapsed !== false}
        />
      );
      break;

    case "textarea":
      control = (
        <Textarea
          {...commonProps}
          rows={ltr ? 8 : 4}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={!ltr}
        />
      );
      break;

    case "number":
      control = (
        <Input
          variant="form"
          type="number"
          {...commonProps}
          value={value ?? ""}
          min={field.min}
          max={field.max}
          step={field.step ?? (field.decimals === 2 || field.precision === 2 ? "0.01" : undefined)}
          onChange={(e) => onChange(e.target.value)}
        />
      );
      break;

    default:
      control = (
        <Input
          variant="form"
          type="text"
          {...commonProps}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={!ltr}
          placeholder={field.key === "cron" ? "0 0 12 * * *" : undefined}
        />
      );
  }

  const colClass =
    field.col === "full"
      ? "col-span-2 max-md:col-span-1"
      : field.col === "half"
        ? "col-span-1"
        : "";

  if (field.type === "code") {
    return (
      <div className={cn("mb-0", colClass)}>
        {control}
        {field.description && (
          <p className="mt-1 text-xs text-slate-500">{field.description}</p>
        )}
        {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      </div>
    );
  }

  if (field.type === "boolean") {
    return (
      <div className={cn("mb-1", colClass)}>
        {control}
        {field.description && (
          <p className="mt-1 text-xs text-slate-500">{field.description}</p>
        )}
        {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      </div>
    );
  }

  return (
    <FormField
      label={label}
      htmlFor={id}
      required={field.required}
      hint={field.description}
      className={cn("mb-0", colClass)}
    >
      {control}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </FormField>
  );
}

export { isLtrField };
